import React, { useEffect, useMemo } from "react";
import { KTable } from "@kyburz/kdesign-system";
import { makeStyles, Theme, Typography } from "@material-ui/core";

import { useTypedSelector } from "../../../../hooks/useTypedSelector";
import useSetState from "../../../../hooks/useTypedSetState";
import { Customer2 } from "../../../../interfaces/ReduxInterface";

import SelectedItems from "../../../fleet/SelectedItems";
import ToggleButton from "../ToggleButton";

const useStyles = makeStyles(({ palette }: Theme) => ({
  title: {
    color: palette.common.black,
    marginBottom: 4,
  },
}));

enum FilterMode {
  All = "all",
  Included = "included",
  NotIncluded = "not included",
}

const filter = {
  [FilterMode.All]: (companies: Customer2[]) => companies,
  [FilterMode.Included]: (
    companies: Customer2[],
    selectedCompanies: string[],
  ) =>
    companies.filter(({ shortname }) => selectedCompanies.includes(shortname)),
  [FilterMode.NotIncluded]: (
    companies: Customer2[],
    selectedCompanies: string[],
  ) =>
    companies.filter(({ shortname }) => !selectedCompanies.includes(shortname)),
};

const filterStates = [
  {
    label: "All",
    value: FilterMode.All,
  },
  {
    label: "Incl.",
    value: FilterMode.Included,
  },
  {
    label: "Not Incl.",
    value: FilterMode.NotIncluded,
  },
];

type CompaniesTabProps = {
  title?: string;
  selectedCompanies: string[];
  onCompaniesChange: (companies: string[]) => void;
};

const CompaniesTab: React.FC<CompaniesTabProps> = ({
  title = "",
  selectedCompanies = [],
  onCompaniesChange = () => {},
}) => {
  const classes = useStyles();

  const { customers: companies } = useTypedSelector((state) => state.customers);

  const [state, setState] = useSetState({
    filterMode: FilterMode.All,
    selected: null as Record<string, boolean> | null,
  });

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        id: "name",
        accessor: "name",
        defaultCanSort: false,
        defaultCanFilter: true,
      },
    ],
    [],
  );

  const filteredCompanies = useMemo(
    () => filter[state.filterMode](companies, selectedCompanies),
    [companies, selectedCompanies, state.filterMode],
  );

  useEffect(() => {
    const included = filteredCompanies
      .map<[boolean, number]>(({ shortname }, index) => [
        selectedCompanies.includes(shortname),
        index,
      ])
      .filter(([value]) => value);

    if (included.length === 0) {
      setState({ selected: {} });
    } else {
      const selected = included.reduce(
        (prev, curr) => ({ ...prev, [curr[1]]: curr[0] }),
        {},
      );

      setState({ selected });
    }
  }, [filteredCompanies, selectedCompanies, setState]);

  const onSelectRows = (_, ids: Record<string, boolean>) => {
    setState({ selected: ids });

    const idsArray = Object.keys(ids);

    if (Object.keys(state.selected || []).length !== idsArray.length) {
      const visibleShortnames = new Set(filteredCompanies.map((c) => c.shortname));
      const preservedCompanies = selectedCompanies.filter(
        (shortname) => !visibleShortnames.has(shortname),
      );
      const newlySelected = idsArray.map(
        (index) => filteredCompanies[parseInt(index, 10)].shortname,
      );

      onCompaniesChange([...preservedCompanies, ...newlySelected]);
    }
  };

  return (
    <>
      <Typography variant="h5" className={classes.title}>
        {title}
      </Typography>

      <ToggleButton
        states={filterStates}
        currectState={state.filterMode}
        onChangeState={(filterMode) => setState({ filterMode })}
      />

      <SelectedItems
        wholeLength={companies.length}
        selectedLength={selectedCompanies.length}
      />

      {state.selected && (
        <KTable
          hasFilter
          columns={columns}
          data={filteredCompanies}
          onSelectRows={onSelectRows}
          initialSelectedRows={state.selected}
        />
      )}
    </>
  );
};

export default CompaniesTab;
